import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@leagueplatform/auth-utils';
import { captureAppError } from '@leagueplatform/observability';
import { FullPageLoadingSpinner } from 'components/loaders/full-page-loading-spinner.component';
import { useUserEligibility } from 'hooks/use-user-eligibility.hook';
import { FullPageError } from 'components/error/full-page-error.component';
import { ErrorTypes } from 'hooks/use-error-message.hook';
import { useIntl } from '@leagueplatform/locales';
import { usePaymentLinkVisibility } from 'hooks/use-payment-link-visibility.hook';
import { SENTRY_CONSTANTS } from 'common/constants';
import {
  EligibilityErrorDiagnostic,
  sendEligibilityErrorDiagnosticEvent,
} from 'utils/analytics-events';

export const AuthSessionContainer = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const lastEligibilityDiagnosticKey = useRef<string | null>(null);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, error: authError } = useAuth();
  const {
    eligible,
    loading,
    error: eligibilityError,
    details: eligibilityDetails,
    errorName: eligibilityErrorName,
    productSubType,
    coverageExperience,
    productSubTypes,
  } = useUserEligibility(isAuthenticated);
  const { isAuthorized } = usePaymentLinkVisibility();
  let eligibilityErrorScreenReason:
    | EligibilityErrorDiagnostic['reason']
    | null = null;

  if (!isAuthenticated) {
    eligibilityErrorScreenReason = null;
  } else if (eligibilityError) {
    eligibilityErrorScreenReason = 'eligibility_fetch_error';
  } else if (eligible === false) {
    eligibilityErrorScreenReason = 'backend_returned_ineligible';
  }

  useEffect(() => {
    if (!eligibilityErrorScreenReason) {
      return;
    }

    const diagnostic: EligibilityErrorDiagnostic = {
      reason: eligibilityErrorScreenReason,
      eligible,
      error: eligibilityError,
      errorName: eligibilityErrorName,
      reasonCode: eligibilityDetails?.reasonCode,
      reasonCodeDescription: eligibilityDetails?.reasonCodeDescription,
      productSubType,
      productSubTypes,
      coverageExperience,
      path: location.pathname,
    };
    const diagnosticKey = [
      diagnostic.reason,
      diagnostic.errorName ?? '',
      diagnostic.reasonCode ?? '',
      diagnostic.productSubType ?? '',
      diagnostic.coverageExperience ?? '',
      diagnostic.path ?? '',
    ].join(':');

    if (lastEligibilityDiagnosticKey.current === diagnosticKey) {
      return;
    }

    lastEligibilityDiagnosticKey.current = diagnosticKey;
    sendEligibilityErrorDiagnosticEvent(diagnostic);
    captureAppError(new Error('Cigna eligibility error screen rendered'), {
      context: {
        Eligibility: { ...diagnostic },
      },
      ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      tags: {
        reason: eligibilityErrorScreenReason,
      },
    });
  }, [
    coverageExperience,
    eligibilityDetails?.reasonCode,
    eligibilityDetails?.reasonCodeDescription,
    eligibilityError,
    eligibilityErrorName,
    eligibilityErrorScreenReason,
    eligible,
    isAuthenticated,
    location.pathname,
    productSubType,
    productSubTypes,
  ]);

  // Check authentication first
  if (isAuthenticated === undefined) {
    return <FullPageLoadingSpinner />;
  }

  // Handle authentication errors or not authenticated state
  if (authError || !isAuthenticated) {
    navigate(
      { pathname: '/' },
      {
        state: {
          redirectPath: location.pathname,
        },
      },
    );
    return null;
  }

  // Then check other loading states
  if (loading || eligibilityError === null || eligible === null) {
    return <FullPageLoadingSpinner />;
  }

  // Handle eligibility errors or ineligibility
  if (eligibilityError || !eligible) {
    return (
      <FullPageError
        errorType={ErrorTypes.NOT_ELIGIBLE}
        customMessage={formatMessage({ id: 'STR_ELIGIBILTY_ERROR' })}
      />
    );
  }

  if (!isAuthorized(location.pathname)) {
    return (
      <FullPageError
        errorType={ErrorTypes.API_ERROR}
        customMessage={formatMessage({ id: 'STR_UNAUTHORIZED_MESSAGE' })}
      />
    );
  }

  return children;
};
